import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import DailyRentalCard from "./DailyRentalCard";
import SevenDayCard from "./SevenDayCard";
import ThirtyDayCard from "./ThirtyDayCard";
import BikeCard from "./BikeCard";
import EvDailyCard from "./EvDailyCard";
import DailyRentalCardMob from "./DailyRentalCard_mob";
import SevenDayCardMob from "./SevenDayCard_mob";
import ThirtyDayCardMob from "./ThirtyDayCard_mob";
import EvDailyCardMob from "./EvDailyCard_mob";
import SearchOverlayMob from "./SearchOverlay_mob";
import FilterSheetMob from "./FilterSheet_mob";
import EvToggle from "../EvToggle/EvToggle";
import BikeFilterSidebar from "./BikeFilterSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Drawer } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import BookingOverlay from "../Homebanner/BookingOverlay";
import { useEvMode } from "../../context/EvModeContext";

// ─── Design tokens ────────────────────────────────────────────────────────────
const PURPLE_GRADIENT = "linear-gradient(2deg, #A28FDC 0.74%, #7C3AED 74.61%)";
const PURPLE_SOLID    = "#7C3AED";
const PURPLE_LIGHT_BG = "#EDE8FC";
const DAY_LABELS      = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function roundToNearest30(t) {
  const mins = t.minute();
  if (mins < 15) return t.minute(0).second(0);
  if (mins < 45) return t.minute(30).second(0);
  return t.add(1, "hour").minute(0).second(0);
}

function roundUpToHour(t) {
  return t.minute() === 0 ? t.second(0) : t.add(60 - t.minute(), "minute").second(0);
}

const atTime = (d, t) => {
  const [h, m] = t.split(":").map(Number);
  return d.hour(h).minute(m).second(0).millisecond(0);
};

// daily ↔ ev share hourly durations; switching between them keeps the drop-off
const modeFamily = m => (m === "daily" || m === "ev" ? "hourly" : m);

// Identifies a searched pickup/drop pair, so a new date or time can be told from a repeat search
const searchRange = (pickup, drop) => `${pickup.format("YYYY-MM-DDTHH:mm")}|${drop.format("YYYY-MM-DDTHH:mm")}`;

function filterCars(data, filters, yearFrom, yearTo) {
  const catalogValues = (() => {
    try { return JSON.parse(localStorage.getItem("catalogValues") || "{}"); } catch { return {}; }
  })();
  const seatingKey = catalogValues?.seatingCapacityLabel || "Seater";

  let filtered = [...(data || [])];
  const keyMap = {
    "Vehicle Body Type": "bodyTypeText",
    [seatingKey]: "seater",
    Seater: "seater",
    Seating: "seater",
    "Transmission Type": "transmissionTypeText",
    Transmission: "transmissionTypeText",
    "Fuel Type": "fuelTypeText",
    Fuel: "fuelTypeText",
    Brand: "brand",
  };
  Object.entries(filters).forEach(([title, values]) => {
    if (values.length > 0 && keyMap[title]) {
      filtered = filtered.filter(car => values.includes(car[keyMap[title]]?.toString()));
    }
  });
  // Model year range
  if (yearFrom) filtered = filtered.filter(car => !car.modelYear || Number(car.modelYear) >= Number(yearFrom));
  if (yearTo)   filtered = filtered.filter(car => !car.modelYear || Number(car.modelYear) <= Number(yearTo));
  return filtered;
}

function buildCalendarDays(month) {
  const start = month.startOf("month");
  const end   = month.endOf("month");
  const days  = [];
  for (let i = 0; i < start.day(); i++) days.push(null);
  for (let d = start; !d.isAfter(end); d = d.add(1, "day")) days.push(d.clone());
  return days;
}

function generateDailySlots() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      slots.push({ value, label: dayjs().hour(h).minute(m).format("h:mm A") });
    }
  }
  return slots;
}

function generateSubSlots() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      slots.push({ value, label: dayjs(`2000-01-01 ${value}`).format("h:mm A") });
    }
  }
  return slots;
}

const DAILY_SLOTS = generateDailySlots();
const SUB_SLOTS   = generateSubSlots();

const SUB_DURATION = { "7days": { min: 7, max: 29 }, "30days": { min: 30, max: 90 } };

const BOOKING_TYPE_KEYS = { daily: "dailyRental", "7days": "shortSubscription", "30days": "subscription", bike: "bikes", ev: "electricCars" };

// Booking type ids differ per environment, so they always come from the catalog
const getCatalogBookingType = (mode) => {
  try {
    const inv = JSON.parse(localStorage.getItem("catalogValues") || "{}")?.bookingTypes?.inverted;
    return Number(inv?.[BOOKING_TYPE_KEYS[mode]]) || null;
  } catch {
    return null;
  }
};

const TABS = [
  { id: "daily",  label: "Daily" },
  { id: "7days",  label: "7 Days+" },
  { id: "30days", label: "30 Days+" },
  { id: "bike",   label: "Bikes" },
  // "ev" is not a visible tab — driven by EvToggle only
];

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// ─── Component ────────────────────────────────────────────────────────────────
const UniversalListing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(window.location.search);

  const ALL_MODES = ["daily", "7days", "30days", "bike", "ev"];
  const [bookingMode, setBookingMode] = useState(() => {
    const m = urlParams.get("mode");
    // bikes are coming soon — a bike URL falls back to cars
    return ALL_MODES.includes(m) && m !== "bike" ? m : "daily";
  });
  const prevNormalMode = useRef(
    ALL_MODES.includes(urlParams.get("mode")) && urlParams.get("mode") !== "ev" && urlParams.get("mode") !== "bike"
      ? urlParams.get("mode")
      : "daily"
  );

  // ── Shared search state ───────────────────────────────────────────────────
  const [cityId,     setCityId]     = useState(urlParams.get("cityId") || "1");
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const cityDropdownRef = useRef(null);
  const cityPortalRef = useRef(null);
  const [cityDropdownPos, setCityDropdownPos] = useState({ top: 0, left: 0 });
  const [hubId,      setHubId]      = useState(urlParams.get("hubid") || "");
  const [startDate,  setStartDate]  = useState(urlParams.get("formatpickupdate") || dayjs().format("YYYY-MM-DD"));
  const [endDate,    setEndDate]    = useState(urlParams.get("formatenddate") || "");
  const [pickupTime, setPickupTime] = useState(urlParams.get("formatpickuptime") || "");
  const [dropTime,   setDropTime]   = useState(urlParams.get("formatdroptime") || "");

  const [pickupDate, setPickupDate] = useState(() => {
    const sd = urlParams.get("startDate") || urlParams.get("formatpickupdate");
    return sd ? dayjs(sd) : roundToNearest30(dayjs().add(3, "hour"));
  });
  const [dropDate, setDropDate] = useState(() => {
    const ed = urlParams.get("endDate") || urlParams.get("formatenddate");
    return ed ? dayjs(ed) : null;
  });

  // ── UI state ─────────────────────────────────────────────────────────────
  const [activePanel,      setActivePanel]      = useState(null); // "pickup" | "dropoff" | null (desktop inline)
  const [calMonth,         setCalMonth]         = useState(() => dayjs().startOf("month"));
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [expandedHeight,   setExpandedHeight]   = useState(160);
  const expandedRef = useRef(null);
  const userExpandedRef = useRef(false);
  const collapsingRef   = useRef(false);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = winWidth < 768;
  const [isFilterOpen,     setIsFilterOpen]     = useState(false);
  const [overlayOpen,      setOverlayOpen]      = useState(false);
  const [overlayStep,      setOverlayStep]      = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Fixed mobile header (tabs + search) — its height offsets the listing below it
  const mobileHeaderRef = useRef(null);
  const [mobileHeaderH, setMobileHeaderH] = useState(0);
  useLayoutEffect(() => {
    const el = mobileHeaderRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setMobileHeaderH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Config ────────────────────────────────────────────────────────────────
  const [bookingCfg,    setBookingCfg]    = useState(null);
  const [distances,     setDistances]     = useState([]);
  const [totalKms,      setTotalKms]      = useState(urlParams.get("freeKmValue") || "140");
  // EV has its own buffer / min hours; subscriptions share the normal buffer
  const isEvMode      = bookingMode === "ev";
  const bufferTime    = (isEvMode ? bookingCfg?.bufferTimeEvCar : bookingCfg?.bufferTime)    ?? 3;
  const minBookingHrs = (isEvMode ? bookingCfg?.minBookingHrsEv : bookingCfg?.minBookingHrs) ?? 18;
  const cfgReady      = bookingCfg?._cityId === String(cityId);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [getcities,    setGetcities]    = useState([]);
  const [hubdetails,   setHubdetails]   = useState([]);
  const [carData,      setCarData]      = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [soldOutData,  setSoldOutData]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [dynamicFilters, setDynamicFilters] = useState([]);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [selectedFilters, setSelectedFilters] = useState({});
  const [sortOption,      setSortOption]      = useState("lowtohigh");
  const [openSections,    setOpenSections]    = useState({});
  const [yearFrom,        setYearFrom]        = useState("");
  const [yearTo,          setYearTo]          = useState("");

  const resetFilters = () => {
    setSelectedFilters({});
    setYearFrom("");
    setYearTo("");
  };

  const isInitialLoad   = useRef(true);
  const prevHubId       = useRef(hubId);
  const pendingHubRef   = useRef("");
  const prevBookingMode = useRef(bookingMode); // tracks actual mode changes, skips initial effect fire
  const fetchSeq        = useRef(0);           // ignore responses from superseded searches
  const lastSearchRange = useRef("");          // pickup/drop of the latest search

  // Bumping this refetches once the batched state updates (dates/mode) have rendered
  const [fetchKey, setFetchKey] = useState(0);
  const requestFetch = () => setFetchKey(k => k + 1);

  // ─── Reinstate URL dates on every navigation to this page ───────────────
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const pt = p.get("formatpickuptime");
    if (!pt) return;
    const sd = p.get("startDate") || p.get("formatpickupdate");
    const ed = p.get("endDate")   || p.get("formatenddate");
    const dt = p.get("formatdroptime");
    if (sd) setPickupDate(dayjs(sd));
    setPickupTime(pt);
    if (p.get("formatpickupdate")) setStartDate(p.get("formatpickupdate"));
    if (ed) setDropDate(dayjs(ed));
    if (dt) setDropTime(dt);
    if (p.get("formatenddate")) setEndDate(p.get("formatenddate"));
  }, [location.search]); // eslint-disable-line

  // ── EV mode context ───────────────────────────────────────────────────────
  const { evMode, setEvMode } = useEvMode();
  const evSyncRef = useRef(false); // prevents circular update

  // An EV URL (e.g. after a refresh) turns the toggle on instead of being flipped back to daily
  useEffect(() => {
    if (bookingMode === "ev" && !evMode) { evSyncRef.current = true; setEvMode(true); }
  }, []); // eslint-disable-line

  // Navbar toggle → switch bookingMode
  useEffect(() => {
    if (evSyncRef.current) { evSyncRef.current = false; return; }
    if (evMode && bookingMode !== "ev") {
      if (bookingMode !== "ev") prevNormalMode.current = bookingMode;
      setBookingMode("ev");
    } else if (!evMode && bookingMode === "ev") {
      setBookingMode(prevNormalMode.current || "daily");
    }
  }, [evMode]); // eslint-disable-line

  // ─── Scroll: collapse to compact pill after scrolling down ───────────────
  useEffect(() => {
    const handle = () => {
      if (userExpandedRef.current) return;
      if (window.scrollY > 150) {
        collapsingRef.current = true;
        setIsSearchExpanded(false);
        setTimeout(() => { collapsingRef.current = false; }, 600);
      } else if (window.scrollY < 40) {
        collapsingRef.current = false;
        setIsSearchExpanded(true);
      }
    };
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  // ─── Measure expanded height so framer-motion animates to exact px ───────
  useLayoutEffect(() => {
    if (expandedRef.current) {
      const h = expandedRef.current.scrollHeight;
      setExpandedHeight(prev => prev === h ? prev : h);
    }
  }, [bookingMode, distances.length]); // eslint-disable-line

  // ─── Close city dropdown on outside click ────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (
        cityDropdownRef.current && !cityDropdownRef.current.contains(e.target) &&
        cityPortalRef.current && !cityPortalRef.current.contains(e.target)
      ) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ─── Init: cities, KM packages, filters ──────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const [cityRes, distRes, filterRes] = await Promise.all([
          fetch(`${API_BASE}/api/cities`),
          fetch(`${API_BASE}/api/rentalpackages/default`),
          fetch(`${API_BASE}/api/filters/allfilters`),
        ]);
        const [cData, dData, fData] = await Promise.all([cityRes.json(), distRes.json(), filterRes.json()]);
        if (cData?.statusCode === "0100") setGetcities(cData.entities.rows.sort((a, b) => a.id - b.id));
        if (dData?.statusCode === "0100") setDistances(dData.entities.rows.sort((a, b) => a - b));
        if (fData?.statusCode === "0100") {
          const raw = fData.dataValues;
          const formatted = Object.keys(raw).map(key => ({
            title: key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()),
            options: Object.values(raw[key].index),
            apiKeys: Object.keys(raw[key].index),
          }));
          setDynamicFilters(formatted);
          const initialOpen = {};
          formatted.forEach(f => (initialOpen[f.title] = true));
          setOpenSections(initialOpen);
        }
      } catch (e) {
        console.error(e);
      }
    };
    init();
  }, []);

  // ─── City change: fetch hubs + booking config + clear filters ────────────
  useEffect(() => {
    if (!cityId) return;
    // Clear filters and results on city change
    const userChange = !isInitialLoad.current;
    if (userChange) {
      resetFilters();
      setCarData([]);
      setOriginalData([]);
      setSoldOutData([]);
      setLoading(true); // show loader (not empty state) until the new hub's results arrive
    }
    fetch(`${API_BASE}/api/hubs?limit=100&cityId=${cityId}`)
      .then(r => r.json())
      .then(data => {
        let newHubId = "";
        if (data?.statusCode === "0100") {
          const rows = data.entities.rows;
          setHubdetails(rows);
          // a hub picked together with the city (mobile search) wins over the default first hub
          const pending = pendingHubRef.current;
          newHubId = pending && rows.some(h => String(h.id) === pending) ? pending : String(rows[0]?.id || "");
          setHubId(newHubId);
        }
        pendingHubRef.current = "";
        // Hub effect only refetches when hubId actually changes — otherwise end the loader here
        if (userChange && (!newHubId || newHubId === prevHubId.current)) setLoading(false);
      })
      .catch(() => { if (userChange) setLoading(false); });
    const reqCityId = String(cityId);
    // A city picked in the search bar resets date/time to its defaults — done by the normalize effect once this loads
    fetch(`${API_BASE}/api/bookingconfigs?cityId=${cityId}`)
      .then(r => r.json())
      .then(data => {
        const row = data?.statusCode === "0100" && data.entities.rows[0];
        setBookingCfg({ ...(row || {}), _cityId: reqCityId });
      })
      .catch(() => setBookingCfg({ _cityId: reqCityId })); // fall back to default rules
  }, [cityId]); // eslint-disable-line

  // ─── Hub change triggers refetch (after city change) ─────────────────────
  useEffect(() => {
    if (prevHubId.current !== hubId && !isInitialLoad.current && hubId) {
      prevHubId.current = hubId;
      getVehicles();
    } else {
      prevHubId.current = hubId;
    }
  }, [hubId]); // eslint-disable-line

  // ─── Keep the selection valid for the current rules (city, EV, mode, clock) ─
  // Defaults when empty or after a city change; otherwise only the parts that break the rules move forward.
  const cityResetRef = useRef(false); // consumed by the normalize effect once the new city's config is in
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const lastNormalizedMode = useRef(bookingMode);
  useEffect(() => {
    if (!cfgReady) return;
    // a new duration shape (hourly ↔ 7 days ↔ 30 days) starts the drop-off over
    const familyChanged = modeFamily(lastNormalizedMode.current) !== modeFamily(bookingMode);
    lastNormalizedMode.current = bookingMode;
    const cityChanged = cityResetRef.current;
    cityResetRef.current = false;

    const sub = SUB_DURATION[bookingMode];
    const bufferStart = dayjs().add(bufferTime, "hour");
    const earliest = (sub ? roundUpToHour(bufferStart) : roundToNearest30(bufferStart)).millisecond(0);
    const current  = pickupDate && pickupTime ? atTime(pickupDate, pickupTime) : null;
    let pickup = cityChanged || !current || current.isBefore(earliest) ? earliest : current;
    if (sub) pickup = roundUpToHour(pickup); // subscription slots are hourly

    const prevDrop = dropDate && dropTime ? atTime(dropDate, dropTime) : null;
    let drop = familyChanged || cityChanged ? null : prevDrop;
    if (sub) {
      drop = drop && atTime(drop, pickup.format("HH:mm")); // drop time mirrors pickup time
      if (!drop || drop.isBefore(pickup.add(sub.min, "day"))) drop = pickup.add(sub.min, "day");
      else if (drop.isAfter(pickup.add(sub.max, "day")))     drop = pickup.add(sub.max, "day");
    } else if (!drop || drop.isBefore(pickup.add(minBookingHrs, "hour"))) {
      drop = pickup.add(minBookingHrs, "hour");
    }

    if (current && pickup.isSame(current) && prevDrop && drop.isSame(prevDrop)) return;
    setPickupDate(pickup);
    setPickupTime(pickup.format("HH:mm"));
    setStartDate(pickup.format("YYYY-MM-DD"));
    setDropDate(drop);
    setDropTime(drop.format("HH:mm"));
    setEndDate(drop.format("YYYY-MM-DD"));
    if (!isInitialLoad.current) requestFetch(); // results were for the old times
  }, [cfgReady, bufferTime, minBookingHrs, bookingMode, tick]); // eslint-disable-line

  // ─── Mode switch: clear filters+results, refetch ──────────────────────────
  useEffect(() => {
    if (!pickupDate || !pickupTime || isInitialLoad.current) return;
    if (prevBookingMode.current === bookingMode) return; // skip initial effect fire / Strict Mode remount
    prevBookingMode.current = bookingMode;
    resetFilters();
    setCarData([]);
    setOriginalData([]);
    setSoldOutData([]);
    // Track prev normal mode for EV toggle
    if (bookingMode !== "ev") prevNormalMode.current = bookingMode;
    requestFetch();
  }, [bookingMode]); // eslint-disable-line

  useEffect(() => {
    if (fetchKey && cityId) getVehicles();
  }, [fetchKey]); // eslint-disable-line

  // ─── Mirror the search into the URL so browser back (e.g. from checkout) restores it ─
  useEffect(() => {
    if (!pickupDate || !pickupTime || !dropDate || !dropTime) return;
    const search = `?${new URLSearchParams({
      mode: bookingMode,
      cityId,
      hubid: hubId,
      formatpickupdate: pickupDate.format("YYYY-MM-DD"),
      formatenddate:    dropDate.format("YYYY-MM-DD"),
      formatpickuptime: pickupTime,
      formatdroptime:   dropTime,
      freeKmValue:      String(totalKms),
    })}`;
    if (search !== window.location.search) navigate(`${location.pathname}${search}`, { replace: true });
  }, [bookingMode, cityId, hubId, pickupDate, pickupTime, dropDate, dropTime, totalKms]); // eslint-disable-line

  // A search for a new date or time starts with clean filters
  const resetFiltersIfRangeChanged = (pickup, drop) => {
    if (searchRange(pickup, drop) !== lastSearchRange.current) resetFilters();
  };

  const handleOverlayConfirm = ({ pickupDate: pd, pickupTime: pt, dropDate: dd, dropTime: dt }) => {
    resetFiltersIfRangeChanged(atTime(pd, pt), atTime(dd, dt));
    requestFetch();
    setPickupDate(pd);
    setPickupTime(pt);
    setDropDate(dd);
    setDropTime(dt);
    setStartDate(pd.format("YYYY-MM-DD"));
    setEndDate(dd.format("YYYY-MM-DD"));
  };

  // Mobile search overlay: city, hub and dates arrive together
  const handleMobileSearchConfirm = ({ cityId: c, hubId: h, pickup, drop }) => {
    resetFiltersIfRangeChanged(pickup, drop);
    setPickupDate(pickup);
    setPickupTime(pickup.format("HH:mm"));
    setStartDate(pickup.format("YYYY-MM-DD"));
    setDropDate(drop);
    setDropTime(drop.format("HH:mm"));
    setEndDate(drop.format("YYYY-MM-DD"));
    if (String(c) !== String(cityId)) {
      // city effect loads the hubs and keeps this hub; the hub change then fetches
      pendingHubRef.current = String(h);
      setCityId(String(c));
    } else if (String(h) !== String(hubId)) {
      setHubId(String(h)); // hub effect fetches
    } else {
      requestFetch();
    }
  };

  // ─── Disabled times ───────────────────────────────────────────────────────
  const getDisabledPickupTimes = () => {
    const disabled = [];
    if (!pickupDate) return disabled;
    if (pickupDate.isSame(dayjs(), "day")) {
      const earliest = roundToNearest30(dayjs().add(bufferTime, "hour"));
      for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
          const slot = pickupDate.hour(h).minute(m);
          if (slot.isBefore(earliest))
            disabled.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
      }
    }
    return disabled;
  };

  const getDisabledDropTimes = () => {
    const disabled = [];
    if (!pickupDate || !pickupTime || !dropDate) return disabled;
    const [ph, pm] = pickupTime.split(":").map(Number);
    const minHours = bookingMode === "7days" ? 7 * 24
      : bookingMode === "30days" ? 30 * 24
      : minBookingHrs; // daily, ev, bike all use config min hrs
    const minDrop = pickupDate.hour(ph).minute(pm).add(minHours, "hour");
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        if (dropDate.hour(h).minute(m).isBefore(minDrop))
          disabled.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      }
    }
    return disabled;
  };

  // ─── API fetch ────────────────────────────────────────────────────────────
  const getVehicles = useCallback(async () => {
    if (!pickupDate || !pickupTime || !dropDate || !dropTime) return;
    if (bookingMode === "bike") return; // bikes are coming soon — never hit the bikes API
    const seq = ++fetchSeq.current;
    setLoading(true);
    const [ph, pm] = pickupTime.split(":").map(Number);
    const [dh, dm] = dropTime.split(":").map(Number);
    const isoStart = pickupDate.hour(ph).minute(pm).format("YYYY-MM-DDTHH:mm");
    const isoEnd   = dropDate.hour(dh).minute(dm).format("YYYY-MM-DDTHH:mm");
    lastSearchRange.current = `${isoStart}|${isoEnd}`;

    try {
      if (bookingMode === "ev") {
        const query = `startDate=${isoStart}&endDate=${isoEnd}&cityId=${cityId}&freeKmValue=${totalKms}`;
        const res  = await fetch(`${API_BASE}/api/v2/vehicle-bookings-v2/search-vehicles-panel-ev-car?${query}`);
        const data = await res.json();
        if (seq !== fetchSeq.current) return; // a newer search is in flight
        if (data?.statusCode === "0100" && Array.isArray(data.dataValues)) {
          const available = data.dataValues.filter(c => !c.isSoldOut);
          setOriginalData(available);
          setSoldOutData(data.dataValues.filter(c => c.isSoldOut));
          applyLocalLogic(available, selectedFilters, sortOption);
        } else {
          setOriginalData([]);
          setCarData([]);
        }
      } else if (bookingMode === "bike") {
        const query = `startDate=${isoStart}&endDate=${isoEnd}&cityId=${cityId}&freeKmValue=${totalKms}`;
        const res  = await fetch(`${API_BASE}/api/v2/vehicle-bookings-v2/search-vehicles-panel-bike?${query}`);
        const data = await res.json();
        if (seq !== fetchSeq.current) return; // a newer search is in flight
        if (data?.statusCode === "0100" && Array.isArray(data.dataValues)) {
          const available = data.dataValues.filter(c => !c.isSoldOut);
          setOriginalData(available);
          setSoldOutData(data.dataValues.filter(c => c.isSoldOut));
          applyLocalLogic(available, selectedFilters, sortOption);
        } else {
          setOriginalData([]);
          setCarData([]);
        }
      } else if (bookingMode === "daily") {
        const query = `startDate=${isoStart}&endDate=${isoEnd}&freeKmValue=${totalKms}&cityId=${cityId}`;
        const res  = await fetch(`${API_BASE}/api/v2/vehicle-bookings-v2/search-vehicles-panel?${query}`);
        const data = await res.json();
        if (seq !== fetchSeq.current) return; // a newer search is in flight
        if (data?.statusCode === "0100") {
          const available = data.dataValues.filter(c => !c.isSoldOut);
          setOriginalData(available);
          setSoldOutData(data.dataValues.filter(c => c.isSoldOut));
          applyLocalLogic(available, selectedFilters, sortOption);
        }
      } else {
        const query = `startDate=${isoStart}&endDate=${isoEnd}&cityId=${cityId}`;
        const res  = await fetch(`${API_BASE}/api/subscriptionbookings/search-availability?${query}`);
        const data = await res.json();
        if (seq !== fetchSeq.current) return; // a newer search is in flight
        if (data?.statusCode === "0100" && Array.isArray(data.dataValues)) {
          setOriginalData(data.dataValues);
          setSoldOutData([]);
          applyLocalLogic(data.dataValues, selectedFilters, sortOption);
        } else {
          setOriginalData([]);
          setCarData([]);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load vehicles. Please try again.");
    } finally {
      if (seq === fetchSeq.current) setTimeout(() => setLoading(false), 1500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingMode, cityId, hubId, pickupDate, pickupTime, dropDate, dropTime, totalKms]);

  // Initial load
  useEffect(() => {
    if (isInitialLoad.current && cityId && hubId) {
      getVehicles();
      isInitialLoad.current = false;
    }
  }, [cityId, hubId, getVehicles]);

  // Apply body type pre-filter from CarCollection navigation (on mount)
  useEffect(() => {
    const bt = sessionStorage.getItem("selectedBodyType");
    if (!bt) return;
    sessionStorage.removeItem("selectedBodyType");
    setSelectedFilters(prev => ({ ...prev, "Vehicle Body Type": [bt] }));
  }, []);

  // Re-fetch on KM package change (daily, ev, bike)
  useEffect(() => {
    if ((bookingMode === "daily" || bookingMode === "ev" || bookingMode === "bike") && cityId && hubId && !isInitialLoad.current) {
      getVehicles();
    }
  }, [totalKms]); // eslint-disable-line

  // ─── Filter logic ─────────────────────────────────────────────────────────
  const applyLocalLogic = useCallback((data, filters, sort) => {
    const filtered = filterCars(data, filters, yearFrom, yearTo);

    filtered.sort((a, b) => {
      if (a.isSoldOut !== b.isSoldOut) return a.isSoldOut ? 1 : -1;
      const priceKey = bookingMode === "daily" ? "baseFare" : "costToTheCustomer";
      const pa = Number(a[priceKey]) || 0;
      const pb = Number(b[priceKey]) || 0;
      return sort === "lowtohigh" ? pa - pb : pb - pa;
    });
    setCarData(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingMode, yearFrom, yearTo]);

  useEffect(() => {
    applyLocalLogic(originalData, selectedFilters, sortOption);
  }, [sortOption, originalData, selectedFilters, applyLocalLogic, yearFrom, yearTo]);

  const handleCheckbox = (title, apiKey) => {
    // Selecting "EV" fuel type switches to EV mode; deselecting reverts
    if (title === "Fuel Type" && apiKey === "EV") {
      const currentlySelected = selectedFilters["Fuel Type"]?.includes("EV");
      if (!currentlySelected) {
        // Switch to EV mode
        evSyncRef.current = true;
        prevNormalMode.current = bookingMode !== "ev" ? bookingMode : prevNormalMode.current;
        setEvMode(true);
        setBookingMode("ev");
        return;
      } else {
        // Deselect EV → revert to normal
        evSyncRef.current = true;
        setEvMode(false);
        setBookingMode(prevNormalMode.current || "daily");
        return;
      }
    }
    setSelectedFilters(prev => {
      const current = prev[title] || [];
      const updated = current.includes(apiKey) ? current.filter(v => v !== apiKey) : [...current, apiKey];
      const next = { ...prev, [title]: updated };
      if (updated.length === 0) delete next[title];
      return next;
    });
  };

  const handleFilterApply = ({ filters, sort, yearFrom: from, yearTo: to }) => {
    setSelectedFilters(filters);
    setSortOption(sort);
    setYearFrom(from);
    setYearTo(to);
    setIsFilterOpen(false);
  };

  const showBikesComingSoon = () =>
    toast.info("Wow Bikez is coming soon!", { position: "top-center", autoClose: 5000, toastId: "bikes-coming-soon" });

  const toggleSection = title => setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));

  // ─── Booking handoff ──────────────────────────────────────────────────────
  const handleBooking = car => {
    const [ph, pm] = (pickupTime || "10:00").split(":").map(Number);
    const [dh, dm] = (dropTime   || "10:00").split(":").map(Number);
    const isoStart = pickupDate.hour(ph).minute(pm).format("YYYY-MM-DDTHH:mm");
    const isoEnd   = dropDate.hour(dh).minute(dm).format("YYYY-MM-DDTHH:mm");

    const isSubscription = bookingMode === "7days" || bookingMode === "30days";
    const bookingType    = car?.bookingType ? Number(car.bookingType) : getCatalogBookingType(bookingMode);

    if (isSubscription) {
      const diffHours = moment(isoEnd, "YYYY-MM-DDTHH:mm").diff(moment(isoStart, "YYYY-MM-DDTHH:mm"), "hours", true);
      const minH = (bookingMode === "7days" ? 7 : 30) * 24;
      if (diffHours < minH) {
        toast.error(`Minimum duration is ${bookingMode === "7days" ? 7 : 30} days`);
        return;
      }
    } else {
      const diffHours = moment(isoEnd, "YYYY-MM-DDTHH:mm").diff(moment(isoStart, "YYYY-MM-DDTHH:mm"), "hours", true);
      if (diffHours < minBookingHrs) {
        toast.error(`Minimum booking duration is ${minBookingHrs} hours`);
        return;
      }
      if (!hubId) { toast.error("Please select a hub to book"); return; }
    }

    const customerId = localStorage.getItem("id");
    const isLoggedIn = localStorage.getItem("islogin") === "true";

    const bookingParams = {
      cityId,
      hubid: car?.hubId || hubId,
      startDate: isSubscription ? isoStart : pickupDate.format("YYYY-MM-DD"),
      endDate:   isSubscription ? isoEnd   : dropDate.format("YYYY-MM-DD"),
      pickupTime,
      dropTime,
      vehicleId:        car?.id,
      source:           "website",
      packageId:        car?.packageId,
      customerId:       customerId || "",
      groupId:          car?.vehicleGroupId,
      seating:          car?.seater,
      transmissiontype: car?.transmissionTypeText,
      fuelType:         car?.fuelTypeText,
      freekm:           car?.totalFreeKms,
      extraKm:          car?.extraKmRental,
      plankm:           totalKms,
      imageUrl:         car?.imageUrl || "",
      bodyTypeText:     car?.bodyTypeText || "",
      isEv:             bookingMode === "ev",
      drivingRange:     car?.drivingRange || "",
      chargingType:     car?.chargingType || "",
      isSubscription,
      bookingType,
    };
    localStorage.setItem("bookingvalues", JSON.stringify(bookingParams));

    if (customerId && isLoggedIn) {
      navigate("/bookings");
    } else {
      // `back` returns a user who closes login to this exact search
      navigate(`/login?${new URLSearchParams({ redirect: "/bookings", back: `${location.pathname}${location.search}` })}`);
    }
  };

  // ─── Duration display ─────────────────────────────────────────────────────
  const getDurationDisplay = () => {
    if (!pickupDate || !pickupTime || !dropDate || !dropTime) return "";
    const [sh, sm] = pickupTime.split(":").map(Number);
    const [eh, em] = dropTime.split(":").map(Number);
    const start = pickupDate.hour(sh).minute(sm);
    const end   = dropDate.hour(eh).minute(em);
    let diff = end.diff(start, "minute");
    if (diff <= 0) return "0 min";
    const days  = Math.floor(diff / 1440); diff -= days * 1440;
    const hours = Math.floor(diff / 60);
    const mins  = diff % 60;
    return [days && `${days}d`, hours && `${hours}h`, mins && `${mins}m`].filter(Boolean).join(" ");
  };

  // ─── Free km: the package is per 24h, scaled to the trip (18h on 140 KM → 105 KM) ──
  const tripHours = pickupDate && pickupTime && dropDate && dropTime
    ? atTime(dropDate, dropTime).diff(atTime(pickupDate, pickupTime), "minute") / 60
    : 0;
  const freeKm = Math.round((Number(totalKms) || 0) * tripHours / 24);

  // ─── Car price helper ─────────────────────────────────────────────────────
  const getCarPrice = car => {
    const raw = bookingMode === "daily" ? car.baseFare : car.costToCustomer;
    const num = Number(raw);
    return { price: isNaN(num) ? 0 : num, unavailable: raw == null || isNaN(num) };
  };

  // ─── Filter sidebar ───────────────────────────────────────────────────────
  const bodyTypeFilter = dynamicFilters.find(f => f.title === "Vehicle Body Type");
  const bodyTypes = bodyTypeFilter
    ? bodyTypeFilter.options.map((opt) => ({ label: opt, key: opt }))
    : ["Hatchback", "Sedan", "SUV", "Crossover SUV", "Compact Sedan", "MUV"].map(l => ({ label: l, key: l }));

  const imageMap = {
    Hatchback: "/assets/collection/hatchback1.png",
    Sedan: "/assets/collection/sedan1.png",
    SUV: "/assets/collection/suv1.png",
    "Crossover SUV": "/assets/collection/crossover-suv1.png",
    "Compact Sedan": "/assets/collection/compact-sedan1.png",
    MUV: "/assets/collection/muv1.png",
    MPV: "/assets/collection/muv1.png",
  };

  const filterSidebarContent = () => (
    <div className="w-full h-full bg-transparent rounded-[24px] flex flex-col font-sans overflow-hidden">
      <div className="w-full px-4 py-3 bg-white flex justify-between items-center shrink-0 shadow-[0_4px_6px_-6px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6.66667 5.33333H9.33333M8 14V8M8 5.33333V2M11.3333 10.6667H14M12.6667 8V2M12.6667 14V10.6667M2 9.33333H4.66667M3.33333 6.66667V2M3.33333 14V9.33333" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="text-[#374151] text-[18px] font-medium">Filter</span>
        </div>
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={() => { setSelectedFilters({}); setSortOption("lowtohigh"); }}
          className="text-[#6b7280] text-[12px] font-medium underline hover:text-[#374151] transition-colors bg-transparent border-none cursor-pointer"
        >
          Clear All
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-0 px-4 py-2" style={{ scrollbarWidth: "thin", scrollbarColor: "#babdee #f3f4f6" }}>
        {/* Sort */}
        <div className="flex items-center gap-2 py-3">
          <span className="text-[13px] font-medium text-black whitespace-nowrap">Sort by :</span>
          <div className="flex-1 bg-white border border-[#e5e7eb] rounded-[8px] flex items-center justify-between px-3 py-1.5">
            <span className="text-[13px] font-medium text-black">{sortOption === "lowtohigh" ? "Low to high" : "High to low"}</span>
            <button
              type="button"
              onClick={() => setSortOption(s => s === "lowtohigh" ? "hightolow" : "lowtohigh")}
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              style={{ background: PURPLE_GRADIENT }}
            >
              {sortOption === "lowtohigh" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <g clipPath="url(#clip0_419_5271)">
                    <path d="M11.4985 0.5H11.9985C11.9985 0.367392 11.9459 0.240215 11.8521 0.146447C11.7583 0.0526784 11.6311 0 11.4985 0V0.5ZM4.49853 14.5L4.14453 14.854C4.19098 14.9006 4.24615 14.9375 4.3069 14.9627C4.36764 14.9879 4.43276 15.0009 4.49853 15.0009C4.5643 15.0009 4.62942 14.9879 4.69017 14.9627C4.75091 14.9375 4.80609 14.9006 4.85253 14.854L4.49853 14.5ZM9.99853 1H11.4985V0L9.99853 0V1ZM10.9985 0.5V6.5H11.9985V0.5H10.9985ZM9.99853 7H12.9985V6H9.99853V7ZM11.8605 8H10.9985V9H11.8605V8ZM12.9725 11.658L13.4545 10.212L12.5065 9.896L12.0245 11.342L12.9725 11.658ZM10.9985 12H12.4985V11H10.9985V12ZM11.9725 14.658L12.9725 11.658L12.0245 11.342L11.0245 14.342L11.9725 14.658ZM8.99853 10C8.99853 10.5304 9.20925 11.0391 9.58432 11.4142C9.95939 11.7893 10.4681 12 10.9985 12V11C10.7333 11 10.479 10.8946 10.2914 10.7071C10.1039 10.5196 9.99853 10.2652 9.99853 10H8.99853ZM10.9985 8C10.4681 8 9.95939 8.21071 9.58432 8.58579C9.20925 8.96086 8.99853 9.46957 8.99853 10H9.99853C9.99853 9.73478 10.1039 9.48043 10.2914 9.29289C10.479 9.10536 10.7333 9 10.9985 9V8ZM11.8605 9C11.9685 8.99981 12.0749 9.02531 12.171 9.0744C12.2672 9.12349 12.3502 9.19476 12.4133 9.28232C12.4765 9.36987 12.5178 9.47119 12.534 9.5779C12.5502 9.68462 12.5408 9.79365 12.5065 9.896L13.4545 10.212C13.5389 9.9594 13.5619 9.69037 13.5218 9.4271C13.4818 9.16384 13.3797 8.91387 13.224 8.69782C13.0683 8.48176 12.8635 8.30582 12.6264 8.18449C12.3893 8.06316 12.1268 7.99993 11.8605 8V9ZM4.85253 14.854L7.85253 11.854L7.14453 11.146L4.14453 14.146L4.85253 14.854ZM4.85253 14.146L1.85253 11.146L1.14453 11.854L4.14453 14.854L4.85253 14.146ZM3.99853 0V14.5H4.99853V0H3.99853Z" fill="white"/>
                  </g>
                  <defs><clipPath id="clip0_419_5271"><rect width="15" height="15" fill="white"/></clipPath></defs>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 13 15" fill="none">
                  <path d="M3 0.145917C3.04645 0.099354 3.10162 0.0624111 3.16237 0.0372047C3.22311 0.0119982 3.28823 -0.000976563 3.354 -0.000976562C3.41977 -0.000976563 3.48489 0.0119982 3.54563 0.0372047C3.60638 0.0624111 3.66155 0.099354 3.708 0.145917L6.708 3.14592L6 3.85392L3.854 1.70692V14.9999H2.854V1.70692L0.708 3.85392L0 3.14592L3 0.145917ZM9.854 0.999917H8.854V-8.27788e-05H10.354C10.4866 -8.27788e-05 10.6138 0.0525957 10.7076 0.146364C10.8013 0.240132 10.854 0.367309 10.854 0.499917V5.99992H11.854V6.99992H8.854V5.99992H9.854V0.999917ZM7.854 9.99992C7.854 9.46948 8.06471 8.96078 8.43979 8.5857C8.81486 8.21063 9.32357 7.99992 9.854 7.99992H10.432C10.7309 7.99975 11.0255 8.07065 11.2915 8.20675C11.5576 8.34286 11.7875 8.54027 11.9622 8.78271C12.137 9.02514 12.2515 9.30566 12.2965 9.6011C12.3415 9.89654 12.3156 10.1985 12.221 10.4819L10.828 14.6579L9.88 14.3419L10.66 11.9999H9.854C9.32357 11.9999 8.81486 11.7892 8.43979 11.4141C8.06471 11.0391 7.854 10.5303 7.854 9.99992ZM10.994 10.9999L11.272 10.1649C11.3164 10.0318 11.3285 9.89012 11.3073 9.75145C11.2862 9.61277 11.2324 9.48111 11.1503 9.36732C11.0683 9.25353 10.9604 9.16088 10.8355 9.097C10.7106 9.03312 10.5723 8.99984 10.432 8.99992H9.854C9.58878 8.99992 9.33443 9.10527 9.14689 9.29281C8.95936 9.48035 8.854 9.7347 8.854 9.99992C8.854 10.2651 8.95936 10.5195 9.14689 10.707C9.33443 10.8946 9.58878 10.9999 9.854 10.9999H10.994Z" fillRule="evenodd" clipRule="evenodd" fill="white"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-neutral-200 shrink-0" />

        {/* Fuel & Transmission — hidden in EV mode */}
        {bookingMode !== "ev" && <>
          <div className="flex flex-col gap-2 py-3">
            <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Fuel Type</span>
            <div className="flex items-center gap-2 flex-wrap">
              {[{ label: "Petrol", key: "Petrol" }, { label: "Diesel", key: "Diesel" }, { label: "EV", key: "EV" }].map(({ label, key }) => {
                const isSelected = key === "EV" ? bookingMode === "ev" : selectedFilters["Fuel Type"]?.includes(key);
                const isEv = key === "EV";
                return (
                  <button key={key} type="button" onClick={() => handleCheckbox("Fuel Type", key)}
                    className={`h-[32px] min-w-[70px] px-3 rounded-[8px] border text-[12px] font-medium transition-all ${
                      isSelected
                        ? isEv
                          ? "border-[#00c6ff] text-[#007a5e] font-bold"
                          : "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold"
                        : "bg-white border-[#e5e5e5] text-[#1a1a1a] hover:border-[#babdee]"
                    }`}
                    style={isSelected && isEv ? { background: "linear-gradient(135deg,rgba(0,198,255,0.12),rgba(0,230,118,0.12))" } : undefined}
                  >
                    {isEv && <span className="mr-1">⚡</span>}{label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="w-full h-px bg-neutral-200 shrink-0" />

          <div className="flex flex-col gap-2 py-3">
            <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Transmission Type</span>
            <div className="flex items-center gap-2 flex-wrap">
              {["Automatic", "Manual"].map(type => {
                const isSelected = selectedFilters["Transmission Type"]?.includes(type);
                return (
                  <button key={type} type="button" onClick={() => handleCheckbox("Transmission Type", type)}
                    className={`h-[32px] min-w-[90px] px-3 rounded-[8px] border text-[12px] font-medium transition-all ${isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border-[#e5e5e5] text-[#1a1a1a] hover:border-[#babdee]"}`}>
                    {type}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="w-full h-px bg-neutral-200 shrink-0" />
        </>}

        {/* Vehicle Type */}
        <div className="flex flex-col gap-2 py-3">
          <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection("VehicleType")}>
            <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Vehicle Type</span>
            <ChevronDown size={15} className={`text-[#374151] transition-transform ${openSections["VehicleType"] === false ? "" : "rotate-180"}`} />
          </div>
          {openSections["VehicleType"] !== false && (
            <div className="grid grid-cols-2 gap-2">
              {bodyTypes.map(({ label, key }) => {
                const isSelected = selectedFilters["Vehicle Body Type"]?.includes(key);
                return (
                  <button key={key} type="button" onClick={() => handleCheckbox("Vehicle Body Type", key)}
                    className={`relative flex flex-col items-center justify-end pb-2 rounded-[10px] border overflow-hidden transition-all h-[100px] ${isSelected ? "border-[#e8273c] bg-[#fff5f6]" : "border-[#e5e7eb] bg-white hover:border-[#9ca3af]"}`}
                  >
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#e8273c] rounded-full flex items-center justify-center">
                        <svg width="9" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                    )}
                    <div className="flex-1 flex items-center justify-center w-full px-2 pt-2">
                      <img src={imageMap[label] || "/assets/sample.png"} alt={label} className="w-full h-[58px] object-contain" onError={e => { e.target.style.display = "none"; }} />
                    </div>
                    <span className={`text-[11px] font-medium leading-none text-center px-1 mt-1 ${isSelected ? "text-[#e8273c]" : "text-[#374151]"}`}>{label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="w-full h-px bg-neutral-200 shrink-0" />

        {/* Seating */}
        <div className="flex flex-col gap-2 py-3">
          <span className="text-[#1a1917] text-[14px] font-semibold tracking-[0.4px]">Seating Capacity</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {[4, 5, 6, 7].map(seat => {
              const isSelected = selectedFilters["Seater"]?.includes(String(seat)) || selectedFilters["Seating"]?.includes(String(seat));
              return (
                <button key={seat} type="button" onClick={() => handleCheckbox("Seater", String(seat))}
                  className={`h-[30px] px-2.5 rounded-[8px] border text-[12px] font-medium transition-all whitespace-nowrap ${isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border-[#e5e5e5] text-[#1a1a1a] hover:border-[#babdee]"}`}>
                  {seat} <span className="text-[10px]">Seats</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-4 w-full shrink-0" />
      </div>
    </div>
  );

  // ─── Desktop inline calendar panel ───────────────────────────────────────
  const allSlots = bookingMode === "daily" ? DAILY_SLOTS : SUB_SLOTS;
  const disabledPickupTimes = getDisabledPickupTimes();
  const disabledDropTimes   = getDisabledDropTimes();
  const calDays             = buildCalendarDays(calMonth);
  const subCfg              = SUB_DURATION[bookingMode];

  const renderCalendarPanel = () => {
    if (!activePanel) return null;
    return (
      <motion.div
        key="ul-panel"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className="hidden xl:block w-full bg-white border-b border-[#e5e7eb] shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
        style={{ position: "absolute", top: "100%", left: 0, zIndex: 200 }}
      >
        <div className="max-w-[900px] mx-auto px-6 py-5 flex gap-8 relative">
          {/* Calendar */}
          <div className="flex-1 min-w-[300px]">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-[#0f172a] font-['Outfit']">{calMonth.format("MMMM YYYY")}</span>
              <div className="flex gap-1">
                <button onClick={() => setCalMonth(m => m.subtract(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft size={15} className="text-[#6b7280]" /></button>
                <button onClick={() => setCalMonth(m => m.add(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRight size={15} className="text-[#6b7280]" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 mb-1">
              {DAY_LABELS.map(d => <div key={d} className="text-center text-[11px] text-[#9ca3af] font-['Outfit'] py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {calDays.map((day, i) => {
                if (!day) return <div key={i} className="h-9" />;
                const isPU    = pickupDate && day.isSame(pickupDate, "day");
                const isDO    = dropDate   && day.isSame(dropDate, "day");
                const inRange = pickupDate && dropDate && day.isAfter(pickupDate, "day") && day.isBefore(dropDate, "day");
                let disabled  = day.isBefore(dayjs(), "day");
                if (!disabled && activePanel === "dropoff" && pickupDate) {
                  if (subCfg) {
                    const minD = pickupDate.add(subCfg.min, "day");
                    const maxD = pickupDate.add(subCfg.max, "day");
                    disabled = day.isBefore(minD, "day") || day.isAfter(maxD, "day");
                  } else {
                    disabled = day.isBefore(pickupDate, "day");
                  }
                }
                let bg = "transparent", color = disabled ? "#d1d5db" : "#374151";
                if (isPU)        { bg = PURPLE_GRADIENT; color = "white"; }
                else if (isDO)   { bg = "#1e1b4b";       color = "white"; }
                else if (inRange){ bg = PURPLE_LIGHT_BG; color = PURPLE_SOLID; }
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      if (activePanel === "pickup") {
                        setPickupDate(day);
                        setStartDate(day.format("YYYY-MM-DD"));
                        setCalMonth(day.startOf("month"));
                        const [ph2, pm2] = (pickupTime || "10:00").split(":").map(Number);
                        const drop2 = subCfg
                          ? day.hour(ph2).minute(pm2).add(subCfg.min, "day")
                          : day.hour(ph2).minute(pm2).add(minBookingHrs, "hour");
                        setDropDate(drop2);
                        setDropTime(drop2.format("HH:mm"));
                        setEndDate(drop2.format("YYYY-MM-DD"));
                      } else {
                        setDropDate(day);
                        setEndDate(day.format("YYYY-MM-DD"));
                        setCalMonth(day.startOf("month"));
                      }
                    }}
                    className="h-9 w-full flex items-center justify-center text-[13px] rounded-full transition-colors font-['Outfit']"
                    style={{ background: bg, color, cursor: disabled ? "not-allowed" : "pointer" }}
                  >
                    {day.date()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="w-px bg-[#e5e7eb] self-stretch shrink-0" />

          {/* Time grid */}
          <div className="w-[280px] shrink-0">
            <p className="text-[#0f172a] text-sm font-semibold font-['Outfit'] mb-3">
              {activePanel === "pickup" ? "Pick-up time" : "Drop-off time"}
            </p>
            <div className="grid grid-cols-4 gap-1.5 max-h-[260px] overflow-y-auto pr-1">
              {allSlots.map(slot => {
                const isDisabled = activePanel === "pickup"
                  ? disabledPickupTimes.includes(slot.value)
                  : disabledDropTimes.includes(slot.value);
                const isActive = activePanel === "pickup" ? pickupTime === slot.value : dropTime === slot.value;
                let btnBg = "white", btnColor = isDisabled ? "#d1d5db" : "#374151", btnBorder = isDisabled ? "#f3f4f6" : "#e5e7eb";
                if (isActive && !isDisabled) { btnBg = PURPLE_GRADIENT; btnColor = "white"; btnBorder = "transparent"; }
                return (
                  <button
                    key={slot.value}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      if (activePanel === "pickup") {
                        setPickupTime(slot.value);
                        if (pickupDate) {
                          const [ph2, pm2] = slot.value.split(":").map(Number);
                          const drop2 = subCfg
                            ? pickupDate.hour(ph2).minute(pm2).add(subCfg.min, "day")
                            : pickupDate.hour(ph2).minute(pm2).add(minBookingHrs, "hour");
                          setDropDate(drop2);
                          setDropTime(drop2.format("HH:mm"));
                          setEndDate(drop2.format("YYYY-MM-DD"));
                        }
                      } else {
                        setDropTime(slot.value);
                      }
                      setActivePanel(null);
                    }}
                    className="py-2 rounded-xl text-[11px] font-['Outfit'] border transition-all"
                    style={{ background: btnBg, color: btnColor, borderColor: btnBorder, cursor: isDisabled ? "not-allowed" : "pointer" }}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={() => setActivePanel(null)} className="absolute top-3 right-4 text-[#9ca3af] hover:text-[#374151] text-xl font-bold">×</button>
        </div>
      </motion.div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  const isSubscriptionMode = bookingMode === "7days" || bookingMode === "30days";
  const cityName = getcities.find(c => String(c.id) === String(cityId))?.name || "";
  const hubName  = hubdetails.find(h => String(h.id) === String(hubId))?.hubName || "";

  return (
    <div className="min-h-screen pb-40 font-sans" style={{ backgroundColor: "#FFFAF9" }}>

      {/* BookingOverlay — standalone modal for date/time/city/hub selection */}
      <BookingOverlay
        open={overlayOpen}
        onClose={() => { setOverlayOpen(false); setOverlayStep(null); }}
        cities={getcities}
        initialTab={bookingMode === "ev" || bookingMode === "bike" ? "daily" : bookingMode}
        initialStep={overlayStep}
        minimal={!!overlayStep}
        onConfirm={handleOverlayConfirm}
        initialValues={{ pickupDate, pickupTime, dropDate, dropTime }}
        bufferTime={bufferTime}
        minBookingHrs={minBookingHrs}
      />

      {/* Mobile: full-screen step-by-step search */}
      <SearchOverlayMob
        open={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
        onConfirm={handleMobileSearchConfirm}
        mode={bookingMode}
        cities={getcities}
        cityId={cityId}
        hubId={hubId}
        hubs={hubdetails}
        bufferTime={bufferTime}
        minBookingHrs={minBookingHrs}
        pickupDate={pickupDate}
        pickupTime={pickupTime}
        dropDate={dropDate}
        dropTime={dropTime}
      />

      {/* ── Search bar ─────────────────────────────────────────────────────── */}
      {/* sticky top-[58px] = flush under the 58px fixed navbar */}
      <div className="fixed top-[58px] left-0 right-0 z-[60]">

        {/* ── Mobile: tab switcher + search summary in one white block ───── */}
        {/* Measured so the listing below starts right under it */}
        <div ref={mobileHeaderRef} className="xl:hidden w-full flex flex-col gap-2 px-2 pt-2 pb-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
          <div className={`rounded-2xl w-full border-2 transition-colors duration-300 ${evMode ? "border-[#4fc3f7]" : "border-[#e5e7eb]"}`}>
            <div className="rounded-[14px] p-1 flex items-center gap-1">
              <div className="flex-1 min-w-0 flex gap-1">
              {TABS.map((t, i) => {
                const mobileActive = bookingMode === t.id || (evMode && t.id === "daily");
                const mobileLocked = (evMode && t.id !== "daily") || t.id === "bike";
                return (
                  <React.Fragment key={t.id}>
                    {i > 0 && <span aria-hidden="true" className="w-px my-1.5 bg-[#d1d5db] shrink-0" />}
                    <button
                      onClick={() => {
                        if (t.id === "bike") { showBikesComingSoon(); return; }
                        if (mobileLocked) { toast.info("Coming soon in EV mode!"); return; }
                        evSyncRef.current = true;
                        setEvMode(false);
                        setCarData([]);
                        setSoldOutData([]);
                        setLoading(true);
                        setBookingMode(t.id);
                      }}
                      className={`flex-1 min-w-0 px-1 py-2 rounded-xl border-t border-l border-r border-b-4 text-[13px] leading-tight font-['Outfit'] font-semibold transition-all ${mobileActive ? "border-black" : "border-transparent"} ${mobileLocked ? "opacity-40 cursor-not-allowed" : ""}`}
                      style={mobileActive
                        ? { background: PURPLE_GRADIENT, color: "white" }
                        : { color: "#6b7280" }}
                    >
                      {t.label}
                    </button>
                  </React.Fragment>
                );
              })}
              </div>
              {/* EV toggle — right end, inside the switcher */}
              <div className="ev-tog-fluid shrink-0 pr-0.5">
                <EvToggle />
              </div>
            </div>
          </div>

          {/* filter + search summary */}
          <div className="flex justify-start items-center gap-2.5 w-full">
          <div
            className="size-14 p-3 bg-neutral-100 rounded-[100px] outline outline-1 outline-offset-[-1px] outline-[#F9BEBA] flex justify-center items-center gap-1 cursor-pointer hover:bg-neutral-200 transition-colors flex-shrink-0"
            onClick={() => setIsFilterOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
              <path d="M12.9334 6.4581H4.69676M1.78943 6.4581H0.600098M1.78943 6.4581C1.78943 6.07265 1.94255 5.70299 2.2151 5.43044C2.48766 5.15788 2.85732 5.00476 3.24276 5.00476C3.62821 5.00476 3.99787 5.15788 4.27043 5.43044C4.54298 5.70299 4.6961 6.07265 4.6961 6.4581C4.6961 6.84355 4.54298 7.21321 4.27043 7.48576C3.99787 7.75831 3.62821 7.91143 3.24276 7.91143C2.85732 7.91143 2.48766 7.75831 2.2151 7.48576C1.94255 7.21321 1.78943 6.84355 1.78943 6.4581ZM12.9334 10.8628H9.10143M9.10143 10.8628C9.10143 11.2483 8.94794 11.6184 8.67533 11.891C8.40271 12.1636 8.03297 12.3168 7.64743 12.3168C7.26198 12.3168 6.89232 12.163 6.61977 11.8904C6.34722 11.6179 6.1941 11.2482 6.1941 10.8628M9.10143 10.8628C9.10143 10.4772 8.94794 10.1078 8.67533 9.8352C8.40271 9.56258 8.03297 9.40943 7.64743 9.40943C7.26198 9.40943 6.89232 9.56255 6.61977 9.8351C6.34722 10.1077 6.1941 10.4773 6.1941 10.8628M6.1941 10.8628H0.600098M12.9334 2.05343H10.8634M7.9561 2.05343H0.600098M7.9561 2.05343C7.9561 1.66798 8.10922 1.29832 8.38177 1.02577C8.65432 0.753216 9.02398 0.600098 9.40943 0.600098C9.60028 0.600098 9.78927 0.637689 9.9656 0.710726C10.1419 0.783763 10.3021 0.890815 10.4371 1.02577C10.572 1.16072 10.6791 1.32094 10.7521 1.49726C10.8252 1.67359 10.8628 1.86258 10.8628 2.05343C10.8628 2.24429 10.8252 2.43327 10.7521 2.6096C10.6791 2.78592 10.572 2.94614 10.4371 3.08109C10.3021 3.21605 10.1419 3.3231 9.9656 3.39614C9.78927 3.46917 9.60028 3.50676 9.40943 3.50676C9.02398 3.50676 8.65432 3.35365 8.38177 3.08109C8.10922 2.80854 7.9561 2.43888 7.9561 2.05343Z" stroke="#374151" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round"/>
            </svg>
          </div>

          <button
            onClick={() => setMobileSearchOpen(true)}
            className="flex-1 px-1 bg-stone-50 rounded-3xl outline outline-1 outline-offset-[-1px] outline-red-200 inline-flex flex-col justify-center items-center overflow-hidden min-w-0"
          >
            <div className="self-stretch p-3.5 border-b border-neutral-200 inline-flex justify-center items-center gap-1.5">
              <div className="size-5 relative overflow-hidden flex-shrink-0 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
                  <path d="M2.86035 15.1201C2.98745 15.0831 3.12412 15.0976 3.24023 15.1611C3.35651 15.2249 3.44334 15.3327 3.48047 15.46C3.51757 15.5872 3.50222 15.7246 3.43848 15.8408C3.3747 15.9569 3.26685 16.043 3.13965 16.0801C2.61635 16.2329 2.2113 16.4016 1.92969 16.5723L1.22168 17.002L1.93164 17.4287C2.22023 17.6021 2.64118 17.7767 3.1875 17.9326C4.39846 18.2786 6.09996 18.5 8 18.5C9.90004 18.5 11.6015 18.2786 12.8125 17.9326C13.3594 17.7755 13.7799 17.602 14.0684 17.4287L14.7773 17.0029L14.0713 16.5732C13.7913 16.4028 13.3879 16.2343 12.8682 16.082C12.7439 16.0433 12.6383 15.9572 12.5762 15.8418C12.514 15.7263 12.4995 15.5908 12.5361 15.4648C12.5728 15.3389 12.658 15.232 12.7725 15.168C12.8851 15.105 13.0179 15.0877 13.1426 15.1201C13.7042 15.2842 14.1992 15.483 14.5879 15.7168C14.9404 15.9521 15.1988 16.2177 15.2998 16.5H15.5C15.5 17.5273 15.1304 17.9212 14.7295 18.1924C14.3129 18.4728 13.7446 18.7068 13.0869 18.8945C11.7644 19.2733 9.96707 19.5 8 19.5C6.03292 19.5 4.23564 19.2733 2.91406 18.8945C2.33714 18.7301 1.82972 18.5302 1.43262 18.2949C0.86813 17.9196 0.5 17.5265 0.5 17C0.5 16.4792 0.859588 16.0894 1.25195 15.8193C1.66095 15.54 2.2174 15.3078 2.86035 15.1201ZM8 0.5C9.85652 0.5 11.6374 1.23705 12.9502 2.5498C14.2629 3.86256 15 5.64348 15 7.5C15 9.87906 13.7022 11.8473 12.2939 13.2891C11.5588 14.0421 10.7426 14.7249 9.8623 15.3291C9.7449 15.4022 9.37085 15.6567 9.00879 15.8779C8.81726 15.9908 8.64893 16.0833 8.51562 16.1475C8.44897 16.1797 8.39758 16.2031 8.35547 16.2188C8.33447 16.2266 8.31738 16.2324 8.30371 16.2363L8 16.2607C7.79149 16.2607 7.5868 16.206 7.40527 16.1035C6.74792 15.7223 6.11198 15.3053 5.50098 14.8535C4.86436 14.3788 4.26342 13.8558 3.70605 13.2891C2.29777 11.8473 1 9.87906 1 7.5C1 5.64348 1.73705 3.86256 3.0498 2.5498C4.36256 1.23705 6.14348 0.5 8 0.5ZM8 5C7.33696 5 6.70126 5.26358 6.23242 5.73242C5.76358 6.20126 5.5 6.83696 5.5 7.5C5.5 8.16304 5.76358 8.79874 6.23242 9.26758C6.70126 9.73642 7.33696 10 8 10C8.66304 10 9.29874 9.73642 9.76758 9.26758C10.2364 8.79874 10.5 8.16304 10.5 7.5C10.5 6.83696 10.2364 6.20126 9.76758 5.73242C9.29874 5.26358 8.66304 5 8 5Z" fill="#ED2D20" stroke="#590600"/>
                </svg>
              </div>
              <div className="flex-1 inline-flex flex-col justify-center items-center gap-1 min-w-0">
                <div className="w-full inline-flex justify-center items-center gap-1.5">
                  <div className="text-zinc-900 text-[clamp(0.8125rem,3.5vw,0.875rem)] font-bold truncate max-w-[90px]">{cityName || "Select City"}</div>
                  <div className="text-zinc-600 text-sm font-medium flex-shrink-0">-</div>
                  <div className="text-neutral-600 text-[clamp(0.75rem,3vw,0.8125rem)] font-semibold truncate max-w-[90px]">{hubName || "Select Hub"}</div>
                </div>
                <div className="w-full inline-flex justify-center items-center gap-1.5">
                  <div className="text-neutral-600 text-[clamp(0.45rem,2.8vw,0.6rem)] font-bold whitespace-nowrap">
                    {pickupDate ? pickupDate.format("DD MMM") : ""}{pickupTime ? `, ${dayjs(`2000-01-01 ${pickupTime}`).format("hh:mm A")}` : ""}
                  </div>
                  <div className="text-neutral-500 text-xs font-semibold flex-shrink-0">-</div>
                  <div className="text-neutral-600 text-[clamp(0.45rem,2.8vw,0.6rem)] font-bold whitespace-nowrap">
                    {dropDate ? dropDate.format("DD MMM") : ""}{dropTime ? `, ${dayjs(`2000-01-01 ${dropTime}`).format("hh:mm A")}` : ""}
                  </div>
                </div>
              </div>
              <div className="size-6 flex justify-center items-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 6L8 10L4 6" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
          </button>
          </div>
        </div>

        {/* ── Desktop: Airbnb-style morphing search band ───────────────────── */}
        {/* Width + height + border-radius all animate together via framer-motion */}
        <motion.div
          className="hidden xl:block overflow-hidden"
          animate={{
            height: isSearchExpanded ? expandedHeight : 56,
            paddingLeft: isSearchExpanded ? 0 : Math.max(0, window.innerWidth / 2 - 280),
            paddingRight: isSearchExpanded ? 0 : Math.max(0, window.innerWidth / 2 - 280),
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: isSearchExpanded ? 0 : 40,
            borderBottomRightRadius: isSearchExpanded ? 0 : 40,
            boxShadow: isSearchExpanded
              ? "none"
              : "0 6px 28px rgba(80, 76, 128, 0.22)",
          }}
          transition={{ duration: 0.48, ease: [0.4, 0, 0.2, 1] }}
          style={{
            width: "100%",
            backgroundColor: "#ffffff",
            overflow: "hidden",
          }}
        >
          {/* ── Expanded content — fades + slides out when compact ── */}
          <motion.div
            animate={{
              opacity: isSearchExpanded ? 1 : 0,
              y: isSearchExpanded ? 0 : -6,
            }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{ pointerEvents: isSearchExpanded ? "auto" : "none" }}
            ref={expandedRef}
          >
          {/* ── Tab switcher row (inside search bar, top) ── */}
          <div className="hidden xl:flex items-center gap-3 px-5 pt-4 pb-3">
            {[
              { id: "daily",  label: "Daily Rental",   sub: "Flexible rentals for Every plan" },
              { id: "7days",  label: "7 Days+",         sub: "Weekly subscription" },
              { id: "30days", label: "30 Days+",        sub: "Monthly subscription" },
              { id: "bike",   label: "Wow bikez",       sub: "Quick rides for Everyday Travel" },
            ].map(tab => {
              const active = bookingMode === tab.id || (evMode && tab.id === "daily");
              const locked = (evMode && tab.id !== "daily") || tab.id === "bike";
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === "bike") { showBikesComingSoon(); return; }
                    if (locked) { toast.info("Coming soon in EV mode!"); return; }
                    evSyncRef.current = true;
                    setEvMode(false);
                    setCarData([]);
                    setSoldOutData([]);
                    setLoading(true);
                    setBookingMode(tab.id);
                  }}
                  className={`w-[160px] h-[60px] px-4 rounded-xl flex items-center justify-between transition-all shrink-0 ${active ? "border-t border-l border-r border-b-4 border-black" : "bg-white border border-[#EAEAEA]"} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
                  style={active ? { background: PURPLE_GRADIENT } : {}}
                >
                  <div className="flex flex-col items-start gap-[2px]">
                    <span className={`text-[15px] font-semibold leading-normal font-['Outfit'] ${active ? "text-white" : "text-[#5E5E5E]"}`}>{tab.label}</span>
                    <span className={`text-[12px] font-medium tracking-[0.12px] font-['Outfit'] text-left ${active ? "text-white/70" : "text-[#777777]"}`}>{tab.sub}</span>
                  </div>
                  {active
                    ? <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="white" strokeWidth="1.5" fill="rgba(255,255,255,0.15)"/><circle cx="10" cy="10" r="5" fill="white"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="#D3D3D3" strokeWidth="1.5" fill="white"/></svg>}
                </button>
              );
            })}

            {/* KM package pills — daily, ev, bike */}
            {(bookingMode === "daily" || bookingMode === "ev" || bookingMode === "bike") && distances.length > 0 && (
              <div className="ml-auto flex flex-col items-center gap-1 shrink-0">
                <span className="text-[#6b7280] text-[11px] font-medium whitespace-nowrap uppercase tracking-wide">KM Package</span>
                <div className="flex items-center gap-2 shrink-0">
                  {distances.map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setTotalKms(d)}
                      className={`h-[32px] px-3 rounded-lg text-[12px] font-semibold transition-all shrink-0 ${
                        String(totalKms) === String(d)
                          ? "text-white shadow-[0px_4px_8px_0px_rgba(124,58,237,0.28)]"
                          : "bg-white border border-[#EAEAEA] text-[#5E5E5E] hover:border-[#7C3AED]"
                      }`}
                      style={String(totalKms) === String(d) ? { background: PURPLE_GRADIENT } : {}}
                    >
                      {d} KM
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="hidden xl:block w-full h-px bg-[#e5e7eb]" />
          {/* ── Fields row ── */}
          <div className="flex flex-row items-center justify-start gap-0 py-[4px] px-[4px]">
          {/* City */}
          <div
            ref={cityDropdownRef}
            className="hidden xl:flex relative items-center gap-3 h-[56px] px-4 py-3 rounded-[28px] cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => {
              if (cityDropdownRef.current) {
                const rect = cityDropdownRef.current.getBoundingClientRect();
                setCityDropdownPos({ top: rect.bottom + 6, left: rect.left });
              }
              setCityDropdownOpen(o => !o);
            }}
          >
            <img src="/assets/mingcute_location-2-fill.svg" alt="" className="w-[36px] h-[36px] object-contain shrink-0" />
            <div className="flex flex-col gap-1 min-w-[80px] items-start">
              <span className="text-[#a7b2c3] text-[14px] font-normal leading-none">City</span>
              <span className="text-[#121212] text-[16px] font-medium leading-none truncate">
                {getcities.find(c => String(c.id) === String(cityId))?.name || ""}
              </span>
            </div>
            <ChevronDown size={16} className={`text-[#374151] shrink-0 transition-transform duration-200 ${cityDropdownOpen ? "rotate-180" : ""}`} />
          </div>
          {cityDropdownOpen && createPortal(
            <div
              ref={cityPortalRef}
              className="fixed z-[9999] bg-white border border-[#e5e7eb] rounded-[16px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] min-w-[180px] py-2 overflow-hidden"
              style={{ top: cityDropdownPos.top, left: cityDropdownPos.left }}
            >
              {getcities.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    if (String(c.id) !== String(cityId)) cityResetRef.current = true;
                    setCityId(String(c.id));
                    setCityDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-[14px] font-['Outfit'] transition-colors hover:bg-[#f5f3ff] ${String(c.id) === String(cityId) ? "text-[#7c3aed] font-semibold bg-[#f5f3ff]" : "text-[#121212] font-medium"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>,
            document.body
          )}

          <div className="hidden xl:block w-px h-full bg-[#c5c6c8] self-stretch" />

          {/* Hub */}
          <div className="hidden xl:flex items-center gap-3 h-[56px] pl-3 pr-4 py-3 rounded-[28px] cursor-pointer hover:bg-gray-50 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M12.75 10.5L33 18V33H12.75V10.5Z" fill="#ED2D20" stroke="#53100B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.75 10.5L3 18V33H12.75" stroke="#53100B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M26.25 33V24L19.5 21.75V33" stroke="#F9F8F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M33 33H12.75" stroke="#53100B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="flex flex-col gap-1 min-w-[100px] items-start">
              <span className="text-[#a7b2c3] text-[14px] font-normal leading-none">City Hub</span>
              <select value={hubId} onChange={e => setHubId(e.target.value)} disabled={hubdetails.length <= 1} className="bg-transparent outline-none appearance-none text-[#121212] text-[16px] font-medium cursor-pointer w-full truncate leading-none max-w-[140px] disabled:cursor-default">
                {hubdetails.map(h => <option key={h.id} value={h.id}>{h.hubName}</option>)}
              </select>
            </div>
            {hubdetails.length > 1 && <ChevronDown size={16} className="text-[#374151] shrink-0 pointer-events-none" />}
          </div>

          <div className="hidden xl:block w-px h-full bg-[#c5c6c8] self-stretch" />

          {/* Pick Up — opens overlay modal */}
          <button
            onClick={() => { setOverlayStep("pickup-date"); setOverlayOpen(true); }}
            className="hidden xl:flex items-center gap-3 h-[56px] px-4 py-3 rounded-[28px] hover:bg-gray-50 transition-colors w-fit justify-start bg-transparent border-none cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
  <path d="M13.2316 14.333C13.6151 14.333 13.9829 14.4645 14.254 14.6985C14.5252 14.9325 14.6775 15.2498 14.6775 15.5807V15.5974C14.6775 16.2861 14.0298 16.8451 13.2316 16.8451H13.2124C12.8289 16.8451 12.4611 16.7136 12.19 16.4797C11.9189 16.2457 11.7665 15.9283 11.7665 15.5974V15.5807C11.7665 15.2498 11.9189 14.9325 12.19 14.6985C12.4611 14.4645 12.8289 14.333 13.2124 14.333H13.2316ZM20.9428 20.9876C21.3263 20.9876 21.694 21.119 21.9652 21.353C22.2363 21.587 22.3887 21.9044 22.3887 22.2353V22.2519C22.3887 22.9407 21.7409 23.4997 20.9428 23.4997H20.9235C20.5401 23.4997 20.1723 23.3682 19.9012 23.1342C19.63 22.9002 19.4777 22.5829 19.4777 22.2519V22.2353C19.4777 21.9044 19.63 21.587 19.9012 21.353C20.1723 21.119 20.5401 20.9876 20.9235 20.9876H20.9428ZM17.0872 20.9876C17.4707 20.9876 17.8385 21.119 18.1096 21.353C18.3808 21.587 18.5331 21.9044 18.5331 22.2353V22.2519C18.5331 22.9407 17.8853 23.4997 17.0872 23.4997H17.068C16.6845 23.4997 16.3167 23.3682 16.0456 23.1342C15.7744 22.9002 15.6221 22.5829 15.6221 22.2519V22.2353C15.6221 21.9044 15.7744 21.587 16.0456 21.353C16.3167 21.119 16.6845 20.9876 17.068 20.9876H17.0872ZM14.6775 22.2353C14.6775 21.9044 14.5252 21.587 14.254 21.353C13.9829 21.119 13.6151 20.9876 13.2316 20.9876H13.2124C12.8289 20.9876 12.4611 21.119 12.19 21.353C11.9189 21.587 11.7665 21.9044 11.7665 22.2353V22.2519C11.7665 22.5829 11.9189 22.9002 12.19 23.1342C12.4611 23.3682 12.8289 23.4997 13.2124 23.4997H13.2316C13.6151 23.4997 13.9829 23.3682 14.254 23.1342C14.5252 22.9002 14.6775 22.5829 14.6775 22.2519V22.2353ZM9.37605 17.6603C9.75952 17.6603 10.1273 17.7917 10.3984 18.0257C10.6696 18.2597 10.8219 18.5771 10.8219 18.908V18.9247C10.8219 19.6134 10.1742 20.1724 9.37605 20.1724H9.35678C8.97331 20.1724 8.60556 20.0409 8.33441 19.8069C8.06326 19.5729 7.91093 19.2556 7.91093 18.9247V18.908C7.91093 18.5771 8.06326 18.2597 8.33441 18.0257C8.60556 17.7917 8.97331 17.6603 9.35678 17.6603H9.37605ZM9.37605 20.9876C9.75952 20.9876 10.1273 21.119 10.3984 21.353C10.6696 21.587 10.8219 21.9044 10.8219 22.2353V22.2519C10.8219 22.9407 10.1742 23.4997 9.37605 23.4997H9.35678C8.97331 23.4997 8.60556 23.3682 8.33441 23.1342C8.06326 22.9002 7.91093 22.5829 7.91093 22.2519V22.2353C7.91093 21.9044 8.06326 21.587 8.33441 21.353C8.60556 21.119 8.97331 20.9876 9.35678 20.9876H9.37605ZM6.96631 18.908C6.96631 18.5771 6.81398 18.2597 6.54283 18.0257C6.27168 17.7917 5.90393 17.6603 5.52046 17.6603H5.50119C5.11772 17.6603 4.74997 17.7917 4.47882 18.0257C4.20767 18.2597 4.05534 18.5771 4.05534 18.908V18.9247C4.05534 19.2556 4.20767 19.5729 4.47882 19.8069C4.74997 20.0409 5.11772 20.1724 5.50119 20.1724H5.52046C5.90393 20.1724 6.27168 20.0409 6.54283 19.8069C6.81398 19.5729 6.96631 19.2556 6.96631 18.9247V18.908ZM10.8219 15.5891C10.8219 14.8953 10.1703 14.333 9.36642 14.333H5.51082C5.12481 14.333 4.7546 14.4653 4.48164 14.7009C4.20868 14.9365 4.05534 15.2559 4.05534 15.5891C4.05534 15.9222 4.20868 16.2417 4.48164 16.4772C4.7546 16.7128 5.12481 16.8451 5.51082 16.8451H9.36642C9.75243 16.8451 10.1226 16.7128 10.3956 16.4772C10.6686 16.2417 10.8219 15.9222 10.8219 15.5891ZM20.9332 17.6603C21.1243 17.6603 21.3136 17.6928 21.4902 17.7559C21.6668 17.819 21.8272 17.9115 21.9624 18.0282C22.0975 18.1448 22.2047 18.2833 22.2779 18.4357C22.351 18.5881 22.3887 18.7514 22.3887 18.9163C22.3887 19.0813 22.351 19.2446 22.2779 19.397C22.2047 19.5494 22.0975 19.6879 21.9624 19.8045C21.8272 19.9211 21.6668 20.0137 21.4902 20.0768C21.3136 20.1399 21.1243 20.1724 20.9332 20.1724H13.222C13.0309 20.1724 12.8416 20.1399 12.665 20.0768C12.4884 20.0137 12.328 19.9211 12.1928 19.8045C12.0577 19.6879 11.9505 19.5494 11.8773 19.397C11.8042 19.2446 11.7665 19.0813 11.7665 18.9163C11.7665 18.7514 11.8042 18.5881 11.8773 18.4357C11.9505 18.2833 12.0577 18.1448 12.1928 18.0282C12.328 17.9115 12.4884 17.819 12.665 17.7559C12.8416 17.6928 13.0309 17.6603 13.222 17.6603H20.9332Z" fill="#ED2D20"/>
  <path d="M28.4993 25.7917C28.4993 25.5486 28.4028 25.3154 28.2309 25.1435C28.059 24.9716 27.8258 24.875 27.5827 24.875C27.3396 24.875 27.1064 24.9716 26.9345 25.1435C26.7626 25.3154 26.666 25.5486 26.666 25.7917V28.4628L27.8513 29.6481C28.0242 29.8151 28.2557 29.9075 28.4961 29.9054C28.7364 29.9033 28.9663 29.8069 29.1363 29.6369C29.3062 29.467 29.4026 29.237 29.4047 28.9967C29.4068 28.7564 29.3144 28.5248 29.1474 28.3519L28.4993 27.7038V25.7917Z" fill="#53100B"/>
  <path d="M6.5 2.41667C6.5 2.17355 6.59658 1.94039 6.76849 1.76849C6.94039 1.59658 7.17355 1.5 7.41667 1.5C7.65978 1.5 7.89294 1.59658 8.06485 1.76849C8.23676 1.94039 8.33333 2.17355 8.33333 2.41667V7C8.33333 7.24311 8.42991 7.47627 8.60182 7.64818C8.77373 7.82009 9.00688 7.91667 9.25 7.91667C9.49311 7.91667 9.72627 7.82009 9.89818 7.64818C10.0701 7.47627 10.1667 7.24311 10.1667 7V4.25H19.3333V2.41667C19.3333 2.17355 19.4299 1.94039 19.6018 1.76849C19.7737 1.59658 20.0069 1.5 20.25 1.5C20.4931 1.5 20.7263 1.59658 20.8982 1.76849C21.0701 1.94039 21.1667 2.17355 21.1667 2.41667V7C21.1667 7.24311 21.2632 7.47627 21.4352 7.64818C21.6071 7.82009 21.8402 7.91667 22.0833 7.91667C22.3264 7.91667 22.5596 7.82009 22.7315 7.64818C22.9034 7.47627 23 7.24311 23 7V4.25H25.75C26.4793 4.25 27.1788 4.53973 27.6945 5.05546C28.2103 5.57118 28.5 6.27065 28.5 7V21.7308C30.1056 21.9629 31.5637 22.7944 32.581 24.0581C33.5983 25.3218 34.0992 26.9238 33.9831 28.5419C33.8669 30.16 33.1423 31.6741 31.9549 32.7795C30.7675 33.8849 29.2056 34.4997 27.5833 34.5C26.5487 34.5008 25.5293 34.2511 24.6122 33.7722C23.6951 33.2933 22.9076 32.5995 22.3171 31.75H3.75C3.02065 31.75 2.32118 31.4603 1.80546 30.9445C1.28973 30.4288 1 29.7293 1 29V7C1 6.27065 1.28973 5.57118 1.80546 5.05546C2.32118 4.53973 3.02065 4.25 3.75 4.25H6.5V2.41667ZM21.1667 28.0833C21.1663 26.5401 21.7221 25.0483 22.7322 23.8816C23.7424 22.7149 25.1392 21.9513 26.6667 21.7308V12.5H2.83333V29C2.83333 29.2431 2.92991 29.4763 3.10182 29.6482C3.27373 29.8201 3.50688 29.9167 3.75 29.9167H21.4325C21.2557 29.3216 21.1661 28.7041 21.1667 28.0833ZM32.1667 28.0833C32.1667 29.2989 31.6838 30.4647 30.8242 31.3242C29.9647 32.1838 28.7989 32.6667 27.5833 32.6667C26.3678 32.6667 25.202 32.1838 24.3424 31.3242C23.4829 30.4647 23 29.2989 23 28.0833C23 26.8678 23.4829 25.702 24.3424 24.8424C25.202 23.9829 26.3678 23.5 27.5833 23.5C28.7989 23.5 29.9647 23.9829 30.8242 24.8424C31.6838 25.702 32.1667 26.8678 32.1667 28.0833Z" fill-rule="evenodd" clip-rule="evenodd" fill="#53100B"/>
</svg>
            <div className="flex flex-col gap-[2px] items-start">
              <span className="text-[#9ca3af] text-[14px] font-normal leading-none">Pick Up</span>
              <span className="text-[#111827] text-[16px] font-medium leading-none whitespace-nowrap">
                {!pickupDate ? "Select pickup date" : !pickupTime ? "Select pickup time" : `${pickupDate.format("DD MMM")} · ${dayjs(`2000-01-01 ${pickupTime}`).format("hh:mm A")}`}
              </span>
            </div>
          </button>

          {/* Duration badge */}
          <div className="hidden xl:flex rounded-full px-[17px] py-1 items-center justify-center gap-2 mx-3 shrink-0" style={{ background: PURPLE_GRADIENT }}>
            <span className="text-white text-[14px] font-medium whitespace-nowrap">{getDurationDisplay() || "-- Hrs"}</span>
          </div>

          {/* Drop Off — opens overlay modal */}
          <button
            onClick={() => { setOverlayStep("dropoff-date"); setOverlayOpen(true); }}
            className="hidden xl:flex items-center gap-3 h-[56px] px-4 py-3 rounded-[28px] hover:bg-gray-50 transition-colors w-fit justify-start bg-transparent border-none cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
  <g clip-path="url(#clip0_242_2333)">
    <path d="M16.2117 14.333C15.8283 14.333 15.4605 14.4645 15.1893 14.6985C14.9182 14.9325 14.7659 15.2498 14.7659 15.5807V15.5974C14.7659 16.2861 15.4136 16.8451 16.2117 16.8451H16.231C16.6145 16.8451 16.9822 16.7136 17.2534 16.4797C17.5245 16.2457 17.6768 15.9283 17.6768 15.5974V15.5807C17.6768 15.2498 17.5245 14.9325 17.2534 14.6985C16.9822 14.4645 16.6145 14.333 16.231 14.333H16.2117ZM8.50053 20.9876C8.11707 20.9876 7.74931 21.119 7.47817 21.353C7.20702 21.587 7.05469 21.9044 7.05469 22.2353V22.2519C7.05469 22.9407 7.70243 23.4997 8.50053 23.4997H8.51981C8.90327 23.4997 9.27103 23.3682 9.54218 23.1342C9.81333 22.9002 9.96566 22.5829 9.96566 22.2519V22.2353C9.96566 21.9044 9.81333 21.587 9.54218 21.353C9.27103 21.119 8.90327 20.9876 8.51981 20.9876H8.50053ZM12.3561 20.9876C11.9727 20.9876 11.6049 21.119 11.3338 21.353C11.0626 21.587 10.9103 21.9044 10.9103 22.2353V22.2519C10.9103 22.9407 11.558 23.4997 12.3561 23.4997H12.3754C12.7589 23.4997 13.1266 23.3682 13.3978 23.1342C13.6689 22.9002 13.8212 22.5829 13.8212 22.2519V22.2353C13.8212 21.9044 13.6689 21.587 13.3978 21.353C13.1266 21.119 12.7589 20.9876 12.3754 20.9876H12.3561ZM14.7659 22.2353C14.7659 21.9044 14.9182 21.587 15.1893 21.353C15.4605 21.119 15.8283 20.9876 16.2117 20.9876H16.231C16.6145 20.9876 16.9822 21.119 17.2534 21.353C17.5245 21.587 17.6768 21.9044 17.6768 22.2353V22.2519C17.6768 22.5829 17.5245 22.9002 17.2534 23.1342C16.9822 23.3682 16.6145 23.4997 16.231 23.4997H16.2117C15.8283 23.4997 15.4605 23.3682 15.1893 23.1342C14.9182 22.9002 14.7659 22.5829 14.7659 22.2519V22.2353ZM20.0673 17.6603C19.6838 17.6603 19.3161 17.7917 19.0449 18.0257C18.7738 18.2597 18.6215 18.5771 18.6215 18.908V18.9247C18.6215 19.6134 19.2692 20.1724 20.0673 20.1724H20.0866C20.47 20.1724 20.8378 20.0409 21.109 19.8069C21.3801 19.5729 21.5324 19.2556 21.5324 18.9247V18.908C21.5324 18.5771 21.3801 18.2597 21.109 18.0257C20.8378 17.7917 20.47 17.6603 20.0866 17.6603H20.0673ZM20.0673 20.9876C19.6838 20.9876 19.3161 21.119 19.0449 21.353C18.7738 21.587 18.6215 21.9044 18.6215 22.2353V22.2519C18.6215 22.9407 19.2692 23.4997 20.0673 23.4997H20.0866C20.47 23.4997 20.8378 23.3682 21.109 23.1342C21.3801 22.9002 21.5324 22.5829 21.5324 22.2519V22.2353C21.5324 21.9044 21.3801 21.587 21.109 21.353C20.8378 21.119 20.47 20.9876 20.0866 20.9876H20.0673ZM22.477 18.908C22.477 18.5771 22.6294 18.2597 22.9005 18.0257C23.1717 17.7917 23.5394 17.6603 23.9229 17.6603H23.9422C24.3256 17.6603 24.6934 17.7917 24.9645 18.0257C25.2357 18.2597 25.388 18.5771 25.388 18.908V18.9247C25.388 19.2556 25.2357 19.5729 24.9645 19.8069C24.6934 20.0409 24.3256 20.1724 23.9422 20.1724H23.9229C23.5394 20.1724 23.1717 20.0409 22.9005 19.8069C22.6294 19.5729 22.477 19.2556 22.477 18.9247V18.908ZM18.6215 15.5891C18.6215 14.8953 19.2731 14.333 20.0769 14.333H23.9325C24.3186 14.333 24.6888 14.4653 24.9617 14.7009C25.2347 14.9365 25.388 15.2559 25.388 15.5891C25.388 15.9222 25.2347 16.2417 24.9617 16.4772C24.6888 16.7128 24.3186 16.8451 23.9325 16.8451H20.0769C19.6909 16.8451 19.3207 16.7128 19.0478 16.4772C18.7748 16.2417 18.6215 15.9222 18.6215 15.5891ZM8.51017 17.6603C8.31904 17.6603 8.12977 17.6928 7.95318 17.7559C7.7766 17.819 7.61614 17.9115 7.48099 18.0282C7.34583 18.1448 7.23862 18.2833 7.16548 18.4357C7.09233 18.5881 7.05469 18.7514 7.05469 18.9163C7.05469 19.0813 7.09233 19.2446 7.16548 19.397C7.23862 19.5494 7.34583 19.6879 7.48099 19.8045C7.61614 19.9211 7.7766 20.0137 7.95318 20.0768C8.12977 20.1399 8.31904 20.1724 8.51017 20.1724H16.2214C16.4125 20.1724 16.6018 20.1399 16.7783 20.0768C16.9549 20.0137 17.1154 19.9211 17.2505 19.8045C17.3857 19.6879 17.4929 19.5494 17.566 19.397C17.6392 19.2446 17.6768 19.0813 17.6768 18.9163C17.6768 18.7514 17.6392 18.5881 17.566 18.4357C17.4929 18.2833 17.3857 18.1448 17.2505 18.0282C17.1154 17.9115 16.9549 17.819 16.7783 17.7559C16.6018 17.6928 16.4125 17.6603 16.2214 17.6603H8.51017Z" fill="#ED2D20"/>
    <path d="M28.4993 25.7917C28.4993 25.5486 28.4028 25.3154 28.2309 25.1435C28.059 24.9716 27.8258 24.875 27.5827 24.875C27.3396 24.875 27.1064 24.9716 26.9345 25.1435C26.7626 25.3154 26.666 25.5486 26.666 25.7917V28.4628L27.8513 29.6481C28.0242 29.8151 28.2557 29.9075 28.4961 29.9054C28.7364 29.9033 28.9663 29.8069 29.1363 29.6369C29.3062 29.467 29.4026 29.237 29.4047 28.9967C29.4068 28.7564 29.3144 28.5248 29.1474 28.3519L28.4993 27.7038V25.7917Z" fill="#53100B"/>
    <path d="M5.33398 2.91667C5.33398 2.67355 5.43056 2.44039 5.60247 2.26849C5.77438 2.09658 6.00754 2 6.25065 2C6.49377 2 6.72692 2.09658 6.89883 2.26849C7.07074 2.44039 7.16732 2.67355 7.16732 2.91667V7.5C7.16732 7.74311 7.26389 7.97627 7.4358 8.14818C7.60771 8.32009 7.84087 8.41667 8.08398 8.41667C8.3271 8.41667 8.56026 8.32009 8.73217 8.14818C8.90407 7.97627 9.00065 7.74311 9.00065 7.5V4.75H18.1673V2.91667C18.1673 2.67355 18.2639 2.44039 18.4358 2.26849C18.6077 2.09658 18.8409 2 19.084 2C19.3271 2 19.5603 2.09658 19.7322 2.26849C19.9041 2.44039 20.0007 2.67355 20.0007 2.91667V7.5C20.0007 7.74311 20.0972 7.97627 20.2691 8.14818C20.441 8.32009 20.6742 8.41667 20.9173 8.41667C21.1604 8.41667 21.3936 8.32009 21.5655 8.14818C21.7374 7.97627 21.834 7.74311 21.834 7.5V4.75H24.584C25.3133 4.75 26.0128 5.03973 26.5285 5.55546C27.0443 6.07118 27.334 6.77065 27.334 7.5V22.2308C28.9396 22.4629 30.3977 23.2944 31.415 24.5581C32.4323 25.8218 32.9332 27.4238 32.8171 29.0419C32.7009 30.66 31.9763 32.1741 30.7889 33.2795C29.6015 34.3849 28.0396 34.9997 26.4173 35C25.3827 35.0008 24.3633 34.7511 23.4462 34.2722C22.5291 33.7933 21.7416 33.0995 21.1511 32.25H2.58398C1.85464 32.25 1.15517 31.9603 0.639441 31.4445C0.123716 30.9288 -0.166016 30.2293 -0.166016 29.5V7.5C-0.166016 6.77065 0.123716 6.07118 0.639441 5.55546C1.15517 5.03973 1.85464 4.75 2.58398 4.75H5.33398V2.91667ZM20.0007 28.5833C20.0003 27.0401 20.5561 25.5483 21.5662 24.3816C22.5764 23.2149 23.9732 22.4513 25.5007 22.2308V13H1.66732V29.5C1.66732 29.7431 1.76389 29.9763 1.9358 30.1482C2.10771 30.3201 2.34087 30.4167 2.58398 30.4167H20.2665C20.0897 29.8216 20.0001 29.2041 20.0007 28.5833ZM31.0007 28.5833C31.0007 29.7989 30.5178 30.9647 29.6582 31.8242C28.7987 32.6838 27.6329 33.1667 26.4173 33.1667C25.2017 33.1667 24.036 32.6838 23.1764 31.8242C22.3169 30.9647 21.834 29.7989 21.834 28.5833C21.834 27.3678 22.3169 26.202 23.1764 25.3424C24.036 24.4829 25.2017 24 26.4173 24C27.6329 24 28.7987 24.4829 29.6582 25.3424C30.5178 26.202 31.0007 27.3678 31.0007 28.5833Z" fill-rule="evenodd" clip-rule="evenodd" fill="#53100B"/>
  </g>
  <defs>
    <clipPath id="clip0_242_2333">
      <rect width="36" height="36" fill="white"/>
    </clipPath>
  </defs>
</svg>
            <div className="flex flex-col gap-[2px] items-start">
              <span className="text-[#9ca3af] text-[14px] font-normal leading-none">Drop Off</span>
              <span className="text-[#111827] text-[16px] font-medium leading-none whitespace-nowrap">
                {!dropDate ? "Select drop date" : !dropTime ? "Select drop time" : `${dropDate.format("DD MMM")} · ${dayjs(`2000-01-01 ${dropTime}`).format("hh:mm A")}`}
              </span>
            </div>
          </button>

          {/* EV toggle pill — fields row */}
          <button
            onClick={() => {
              const next = !evMode;
              evSyncRef.current = true;
              setEvMode(next);
              if (next) {
                prevNormalMode.current = bookingMode !== "ev" ? bookingMode : prevNormalMode.current;
                setBookingMode("ev");
              } else {
                setBookingMode(prevNormalMode.current || "daily");
              }
            }}
            className="hidden xl:flex h-[40px] px-4 rounded-xl items-center gap-2 border-2 transition-all shrink-0 font-['Outfit'] ml-auto"
            style={evMode
              ? { background: "linear-gradient(135deg,#001a12 0%,#003a1e 100%)", borderColor: "#00c6ff", boxShadow: "0 0 18px rgba(0,198,255,0.25)" }
              : { background: "white", borderColor: "#E0F7EF" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={evMode ? "#00e676" : "#9ca3af"}><path d="M13 2L4.5 13.5H11L10 22L19.5 10.5H13L13 2Z"/></svg>
            <div className="flex flex-col items-start gap-[1px]">
              <span className={`text-[13px] font-semibold leading-none ${evMode ? "text-[#00e676]" : "text-[#5E5E5E]"}`}>EV Mode</span>
              <span className={`text-[10px] font-medium ${evMode ? "text-[#00c6ff]/70" : "text-[#aaa]"}`}>{evMode ? "Electric only" : "Switch to EV"}</span>
            </div>
            <div
              className="ml-1 w-8 h-5 rounded-full relative transition-all flex-shrink-0"
              style={{ background: evMode ? "linear-gradient(135deg,#00c6ff,#00e676)" : "#e5e7eb" }}
            >
              <div
                className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all"
                style={{ left: evMode ? "calc(100% - 18px)" : "2px" }}
              />
            </div>
          </button>

          <div className="hidden xl:block w-px h-full bg-[#c5c6c8] self-stretch mx-2" />

          {/* Search button — purple gradient matching tabs */}
          <button
            onClick={() => {
              if (pickupDate && pickupTime && dropDate && dropTime) {
                resetFiltersIfRangeChanged(atTime(pickupDate, pickupTime), atTime(dropDate, dropTime));
              }
              getVehicles();
              setActivePanel(null);
            }}
            className="hidden xl:flex items-center justify-center w-[56px] h-[56px] rounded-full shrink-0 hover:opacity-90 transition-all active:scale-95 mx-2"
            style={{ background: PURPLE_GRADIENT }}
            title="Check Availability"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
          </div>{/* end fields row */}
          <div className="pb-4" />
          </motion.div>{/* end expanded content motion.div */}

          {/* ── Compact pill content — fades + scales in when collapsed ── */}
          <motion.div
            animate={{
              opacity: isSearchExpanded ? 0 : 1,
              scale: isSearchExpanded ? 0.92 : 1,
            }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{
              pointerEvents: isSearchExpanded ? "none" : "auto",
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "56px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              onClick={() => {
                userExpandedRef.current = true;
                window.scrollTo({ top: 0, behavior: "smooth" });
                const waitForTop = () => {
                  if (window.scrollY < 20) {
                    setIsSearchExpanded(true);
                    userExpandedRef.current = false;
                  } else {
                    requestAnimationFrame(waitForTop);
                  }
                };
                requestAnimationFrame(waitForTop);
              }}
              className="h-full w-full flex items-center justify-center gap-4 bg-transparent border-none cursor-pointer"
            >
              <span className="text-[#121212] text-[20px] font-semibold whitespace-nowrap">{cityName || "Select City"}</span>
              <span className="w-px h-5 bg-[#d1d5db] shrink-0" />
              <span className="text-[#6b7280] text-[18px] font-medium whitespace-nowrap">
                {pickupDate ? pickupDate.format("DD MMM") : "Pickup"} – {dropDate ? dropDate.format("DD MMM") : "Drop"}
              </span>
              <span className="w-px h-5 bg-[#d1d5db] shrink-0" />
              <span className="text-[#6b7280] text-[18px] font-medium whitespace-nowrap">
                {bookingMode === "daily" ? "Daily Rental" : bookingMode === "7days" ? "7 Days+" : "30 Days+"}
              </span>
              {bookingMode === "daily" && totalKms && (
                <>
                  <span className="w-px h-5 bg-[#d1d5db] shrink-0" />
                  <span className="text-[18px] font-semibold whitespace-nowrap" style={{ color: PURPLE_SOLID }}>{totalKms} KM</span>
                </>
              )}
              <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 ml-2" style={{ background: PURPLE_GRADIENT }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
            </button>
          </motion.div>

          {/* Desktop inline calendar panel */}
          {isSearchExpanded && renderCalendarPanel()}
        </motion.div>{/* end morphing desktop band */}
      </div>

      {/* ── Mode-specific controls ──────────────────────────────────────────── */}
      <div className="max-w-[1450px] min-[1880px]:max-w-none min-[1880px]:px-8 mx-auto px-4 py-2 relative z-10 mt-4 md:mt-6" style={{ marginTop: winWidth >= 1280 ? (isSearchExpanded ? expandedHeight + 64 : 120) : 58 + mobileHeaderH + 8, transition: "margin-top 0.48s cubic-bezier(0.4,0,0.2,1)" }}>

        {/* Mobile KM pills — only shown on mobile when daily rental */}
        {!isSubscriptionMode && (
          <div className="self-stretch flex flex-col gap-3 xl:hidden mb-2">
            <div className="text-center pt-2 text-zinc-900 text-sm font-bold tracking-tight">Choose Vehicle Package</div>
            <div className="pb-4 flex justify-center items-center gap-2 flex-wrap">
              {distances.map(d => (
                <button key={d} type="button" onClick={() => setTotalKms(d)}
                  className="flex-shrink-0 min-w-[80px] py-1.5 rounded-lg flex justify-center items-center gap-1 transition-all duration-200"
                  style={String(totalKms) === String(d)
                    ? { background: PURPLE_GRADIENT, boxShadow: "0 10px 20px 0 rgba(91,76,219,0.30)" }
                    : { background: "white", outline: "1px solid #e5e7eb" }}>
                  <span className={`text-[clamp(0.7rem,2.8vw,0.9rem)] font-semibold ${String(totalKms) === String(d) ? "text-white" : "text-zinc-900"}`}>{d} KM</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Main content ────────────────────────────────────────────────── */}
        <div className="flex flex-col xl:flex-row gap-6">
          <aside className="hidden xl:block w-66 flex-shrink-0">
            <div className="sticky top-[110px] z-30 h-[calc(100vh-140px)] overflow-hidden rounded-[24px]">
              {bookingMode === "bike" ? (
                <BikeFilterSidebar
                  originalData={originalData}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  sortOption={sortOption}
                  setSortOption={setSortOption}
                  yearFrom={yearFrom}
                  setYearFrom={setYearFrom}
                  yearTo={yearTo}
                  setYearTo={setYearTo}
                />
              ) : filterSidebarContent()}
            </div>
          </aside>

          <main className="flex-1 w-full relative z-10">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32">
                <img src="/assets/bg1.gif" alt="Loading..." className="w-[280px] h-[280px] object-contain" />
                <p className="text-[#6B7280] text-base font-medium font-['Outfit'] mt-2 animate-pulse">Finding the Right Vehicles for You ......</p>
              </div>
            ) : [...carData, ...soldOutData].length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <img src="/assets/no-results.png" alt="No vehicles found" className="w-[469px] h-[312px] object-contain max-w-full" />
                <span className="text-[#6B7280] text-base font-medium font-['Outfit']">No vehicles available for this search.</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 min-[1880px]:grid-cols-3 gap-6">
                {[...carData, ...soldOutData].map((car, i) => {
                  if (bookingMode === "daily") {
                    if (isMobile) {
                      return (
                        <DailyRentalCardMob
                          key={car.id || i}
                          car={car}
                          freeKm={freeKm}
                          onBook={handleBooking}
                        />
                      );
                    }
                    return (
                      <DailyRentalCard
                        key={car.id || i}
                        car={car}
                        freeKm={freeKm}
                        onBook={handleBooking}
                        winWidth={winWidth}
                      />
                    );
                  }
                  if (bookingMode === "7days") {
                    const Card = isMobile ? SevenDayCardMob : SevenDayCard;
                    return (
                      <Card
                        key={car.id || i}
                        car={car}
                        onBook={handleBooking}
                      />
                    );
                  }
                  if (bookingMode === "30days") {
                    const Card = isMobile ? ThirtyDayCardMob : ThirtyDayCard;
                    return (
                      <Card
                        key={car.id || i}
                        car={car}
                        onBook={handleBooking}
                      />
                    );
                  }
                  if (bookingMode === "bike") {
                    return (
                      <BikeCard
                        key={car.id || i}
                        car={car}
                        onBook={handleBooking}
                      />
                    );
                  }
                  // EV mode — use dedicated EV card
                  if (isMobile) {
                    return (
                      <EvDailyCardMob
                        key={car.id || i}
                        car={car}
                        onBook={handleBooking}
                      />
                    );
                  }
                  return (
                    <EvDailyCard
                      key={car.id || i}
                      car={car}
                      onBook={handleBooking}
                      winWidth={winWidth}
                    />
                  );
                })}
              </div>
            )}

            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-20 right-5 z-50 bg-[#ED2D20] text-white p-3 rounded-full shadow-lg hover:bg-red-800 transition-all"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          </main>
        </div>
      </div>

      {/* Mobile filter drawer */}
      <Drawer
        placement="left"
        onClose={() => setIsFilterOpen(false)}
        open={isFilterOpen}
        width="85vw"
        closable={false}
        destroyOnClose
        zIndex={16000161} // above the WhatsApp widget (z-index 16000160), which would cover Cancel
        styles={{
          wrapper: { boxShadow: "none" },
          content: { borderRadius: "0 32px 32px 0", boxShadow: "0 -8px 24px rgba(0,0,0,0.1)" },
          body: { padding: 0, overflow: "hidden" },
        }}
      >
        {bookingMode === "bike" ? (
          <BikeFilterSidebar
            originalData={originalData}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            sortOption={sortOption}
            setSortOption={setSortOption}
            yearFrom={yearFrom}
            setYearFrom={setYearFrom}
            yearTo={yearTo}
            setYearTo={setYearTo}
          />
        ) : (
          <FilterSheetMob
            originalData={originalData}
            bodyTypes={bodyTypes}
            isEvMode={isEvMode}
            selectedFilters={selectedFilters}
            sortOption={sortOption}
            yearFrom={yearFrom}
            yearTo={yearTo}
            countCars={(filters, from, to) => filterCars(originalData, filters, from, to).length}
            onApply={handleFilterApply}
            onCancel={() => setIsFilterOpen(false)}
          />
        )}
      </Drawer>
    </div>
  );
};

export default UniversalListing;
